// Remembers the ENTRY CODE a visitor arrived with — the `utm_content` of the
// link (`/scenarios/841?utm_content=<CODE>`) — so the run they start records it
// as `runs.entry_code` (#3842 follow-up, migration 0532).
//
// WHY NOT lib/entrySource.js: that snapshot is sent only when a NEW guest is
// minted (`users.guest_entry_source`). A browser already holding a guest token
// reuses it, so a returning applicant's code, or the second applicant's code on
// a shared phone, never reached the server (prod run 10324). This value goes on
// every POST /api/runs from ScenarioDetailPage instead. entrySource's own
// behaviour is unchanged. Its credential redactor is also the wrong rule here:
// it would turn a legal 40-64 character code into "[redacted]".
//
// CAPTURED AT BOOT (module import from AuthContext, before React mounts), for
// the same reason as entrySource.js: by the time the learner presses Start the
// query string may be gone.
//
// LIFECYCLE (per document load):
//   - the load carries a `utm_content` param, or any other `utm_*` param: this
//     is a new entry, so it REPLACES what is held — with the code when valid,
//     with nothing when absent or invalid (a malformed link must not inherit
//     the previous applicant's code);
//   - the load carries no `utm_*` param (a reload, a bare URL): what is held is
//     kept.
// Held in module memory (primary: sessionStorage can throw in Safari private
// mode) and in sessionStorage (carries it across a reload in this tab only).
//
// THE SERVER IS AUTHORITATIVE: backend/src/services/runEntryCode.js re-validates
// with the same rule, and the column has the same CHECK. This copy exists so an
// invalid value never reaches storage or the network.
//
// Rule (COPY of the backend's): 1-64 characters of [A-Za-z0-9_-]; anything else
// is dropped silently. Never logged, never shown, never a prompt input.

const STORAGE_KEY = 'tacit_entry_code';
const VERSION = 1;
export const ENTRY_CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** The code verbatim, or null when it is not a valid code. COPY of normaliseEntryCode. */
export function normaliseEntryCode(raw) {
  return typeof raw === 'string' && ENTRY_CODE_PATTERN.test(raw) ? raw : null;
}

/**
 * Read one query string. `isEntry` = it carries any `utm_*` param (keys matched
 * case-insensitively, as entrySource.js does); `code` = the FIRST
 * `utm_content`, validated (null when absent or invalid).
 */
export function readEntryCode(search) {
  let params;
  try {
    params = new URLSearchParams(String(search ?? ''));
  } catch {
    return { isEntry: false, code: null };
  }
  let isEntry = false;
  let raw;
  for (const [key, value] of params) {
    const lower = key.toLowerCase();
    if (!lower.startsWith('utm_')) continue;
    isEntry = true;
    if (lower === 'utm_content' && raw === undefined) raw = value;
  }
  return { isEntry, code: normaliseEntryCode(raw) };
}

function storageRead(win) {
  try { return win.sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function storageWrite(win, code) {
  try {
    if (code === null) win.sessionStorage.removeItem(STORAGE_KEY);
    else win.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ v: VERSION, code }));
  } catch {
    // memory is primary
  }
}

// Storage is not trusted (an extension or an older build could write anything).
function normaliseStored(rawJson) {
  if (typeof rawJson !== 'string') return null;
  try {
    const parsed = JSON.parse(rawJson);
    if (!parsed || typeof parsed !== 'object' || parsed.v !== VERSION) return null;
    return normaliseEntryCode(parsed.code);
  } catch {
    return null;
  }
}

// undefined = not decided for this module instance yet (fall back to storage).
let held;

/** Decide this document load's code and remember it. Exported for tests. */
export function captureEntryCode(win = (typeof window !== 'undefined' ? window : undefined)) {
  if (!win) return null;
  const { isEntry, code } = readEntryCode(win.location?.search);
  if (isEntry) {
    held = code;
    storageWrite(win, code);
    return held;
  }
  held = normaliseStored(storageRead(win));
  return held;
}

/** The code to send as `body.entryCode`, or null. */
export function getEntryCode() {
  try {
    if (held !== undefined) return held;
    return typeof window !== 'undefined' ? normaliseStored(storageRead(window)) : null;
  } catch {
    return null;
  }
}

/**
 * Put the held code on a POST /api/runs body as `entryCode`. Mutates and
 * returns `body`; adds nothing when no valid code is held.
 */
export function attachEntryCodeToBody(body) {
  const code = getEntryCode();
  if (code !== null && body && typeof body === 'object') body.entryCode = code;
  return body;
}

/** Test seam: forget the in-memory value (not storage). */
export function __resetEntryCodeForTests() {
  held = undefined;
}

// Boot capture. Imported by AuthContext at app start, so a throw here would
// take the app down over an attribution field: swallow it.
try {
  if (typeof window !== 'undefined') captureEntryCode(window);
} catch {
  // never block boot on entry-code capture
}
